import { useEffect, useState, useCallback, useMemo } from 'react'

// Router
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

// Services
import { useGetProcedures } from 'lib/services/api/reports-service/preference-card/getProcedures'
import { useGetPreferenceCards } from 'lib/services/api/reports-service/preference-card/getPreferenceCards'
import { useGetAllPreferenceCards } from 'lib/services/api/reports-service/preference-card/getAllPreferenceCards'
import { useDeletePrefCard } from 'lib/services/api/reports-service/preference-card/deletePreferenceCard'
import { useUnAssignPreferenceCard } from 'lib/services/api/reports-service/preference-card/unAssignPreferenceCard'
import { useGetSurgeons } from 'lib/services/api/reports-service/preference-card/getSurgeons'
import { useGetProcedureTypes } from 'lib/services/api/reports-service/preference-card/getProcedureTypes'
import { useListSurgeriesWithPreferenceCards } from 'lib/services/api/reports-service/getUsageReports'
import { useCreateBetterIdRequest } from 'lib/services/config/betterId-config'

// Types
import { SurgeryData } from 'lib/services/api/reports-service/preference-card/getProcedures/types'
import { PrefCardsResponseItem } from 'lib/services/api/reports-service/preference-card/getPreferenceCards/types'
import {
  IPrefCardAction,
  ExportAllPrefCardsState,
  SearchParams,
  SurgeonOption,
  ProcedureTypeOption,
  DateRangeType,
} from './PreferenceCards.types'

// Components
import Button from 'components/molecules/Button/Button'

// MUI
import {
  Delete,
  ReadMore,
  AssignmentInd,
  ContentCopy,
} from '@mui/icons-material'
import { TableRowProps, Typography } from '@mui/material'

// Constants
import { prefCardHeadCells, proceduresHeadCells } from './constants'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'

// Other
import dayjs from 'lib/dayjs'
import { debounce } from 'lodash'
import { enqueueSnackbar } from 'notistack'
import { pdf } from '@react-pdf/renderer'
import PreferenceCardsPDF from './PreferenceCardsPDF'

const useLogic = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { createBetterIdRequest } = useCreateBetterIdRequest()
  const { showPascPreferenceCardFeatures } = useOrganizationSettings()
  const initialdate = dayjs().endOf('day').toDate()
  const [searchName, setSearchName] = useState('')
  const [searchSurgeon, setSearchSurgeon] = useState<string>()
  const [searchProcedure, setSearchProcedure] = useState<string>()
  const [beforeDate, setBeforeDate] = useState(initialdate)
  const [afterDate, setAfterDate] = useState(initialdate)
  const [isAlertOpen, setIsAlertOpen] = useState<IPrefCardAction>({
    isOpen: false,
    type: 'delete',
  })
  const [searchParams, setSearchParams] = useState<SearchParams>()
  const [isAssignModalOpen, setIsAssignModalOpen] = useState<boolean>(false)
  const [selectedSurgery, setSelectedSurgery] = useState<SurgeryData | null>(
    null
  )
  const [activeTab, setActiveTab] = useState<number>(() => {
    const savedTab = localStorage.getItem('preferenceCardsActiveTab')
    return savedTab ? parseInt(savedTab) : 0
  })
  const [procedurePage, setProcedurePage] = useState(0)
  const [prefCardPage, setPrefCardPage] = useState(0)
  const [procedureRowsPerPage, setProcedureRowsPerPage] = useState(10)
  const [prefCardRowsPerPage, setPrefCardRowsPerPage] = useState(10)
  const [selectedProcedureRows, setSelectedProcedureRows] = useState<string[]>(
    []
  )
  const [selectedProcedureData, setSelectedProcedureData] = useState<
    SurgeryData[]
  >([])
  const [selectedPrefCardRows, setSelectedPrefCardRows] = useState<string[]>([])
  const [selectedPrefCardData, setSelectedPrefCardData] = useState<
    PrefCardsResponseItem[]
  >([])

  // State for preference cards export
  const [exportState, setExportState] = useState({
    today: false,
    tomorrow: false,
    dateRange: false,
  })
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [exportAllPrefCardsState, setExportAllPrefCardsState] =
    useState<ExportAllPrefCardsState>({
      all: false,
    })

  // Services
  const {
    data: prefCards,
    isLoading: isLoadingPrefCards,
    isError: isErrorPrefCard,
  } = useGetPreferenceCards({
    name: searchParams?.name,
    page: prefCardPage + 1,
    pageSize: prefCardRowsPerPage,
    surgeonName: searchParams?.surgeonName,
    procedureType: searchParams?.procedureType,
    enabled: activeTab === 1, // Only fetch when on preference cards tab
  })

  const { refetch: refetchTodayProcedures } =
    useListSurgeriesWithPreferenceCards({
      startDate: dayjs().startOf('day'),
      endDate: dayjs().endOf('day'),
      enabled: false,
    })

  const { refetch: refetchTomorrowProcedures } =
    useListSurgeriesWithPreferenceCards({
      startDate: dayjs().add(1, 'day').startOf('day'),
      endDate: dayjs().add(1, 'day').endOf('day'),
      enabled: false,
    })

  const { refetch: refetchDateRangeProcedures } =
    useListSurgeriesWithPreferenceCards({
      startDate: dayjs(afterDate),
      endDate: dayjs(beforeDate),
      enabled: false,
    })

  const { mutateAsync: deletePrefCard, isLoading: isDeleting } =
    useDeletePrefCard()
  const { mutateAsync: unAssignPrefCard, isLoading: isUnAssigning } =
    useUnAssignPreferenceCard()
  const {
    data: procedures,
    isLoading: isLoadingProcedures,
    isError,
  } = useGetProcedures({
    startDate: dayjs(afterDate).format('YYYY-MM-DD'),
    endDate: dayjs(beforeDate).format('YYYY-MM-DD'),
    pageSize: procedureRowsPerPage,
    page: procedurePage + 1,
    checkPreferenceCard: true,
  })
  const { data: surgeonsData, isLoading: isLoadingSurgeons } = useGetSurgeons()
  const surgeonsOptions = surgeonsData?.surgeons.map((item) => ({
    id: item.id,
    name: `${item.firstName} ${item.lastName}`,
  })) as SurgeonOption[]
  const { data: procedureTypesData, isLoading: isLoadingProcedureTypes } =
    useGetProcedureTypes()
  const procedureTypesOptions = procedureTypesData?.procedures
    ?.filter((item) => item)
    .map((item, index) => ({
      id: index,
      name: item,
    })) as ProcedureTypeOption[]

  const rawPreferenceCards = prefCards?.preference_cards || []

  // Set up the hook for all preference cards, but don't fetch automatically
  const { refetch: refetchAllPrefCards } = useGetAllPreferenceCards({
    enabled: false,
  })

  const handleCardClick = useCallback(
    (id: string) => {
      navigate(`/preference-card/${id}`)
    },
    [navigate]
  )

  const handleCloneClick = useCallback(
    (id: string) => {
      navigate(`/preference-card/${id}/clone`)
    },
    [navigate]
  )

  const preferenceCards = useMemo(
    () =>
      rawPreferenceCards?.map((item) => ({
        id: item.id,
        surgeon:
          item.surgeon && item.surgeon.firstName && item.surgeon.lastName
            ? `${item.surgeon.firstName}, ${item.surgeon.lastName}`
            : '-',
        procedure: item.procedure_type || '-',
        name: item.name || 'No Name Available',
        actions: (
          <div className="table-actions">
            <Button
              data-testid="delete-pref-card-button"
              onClick={() =>
                setIsAlertOpen({ isOpen: true, id: item.id, type: 'delete' })
              }
              endIcon={<Delete color="error" />}
            >
              <Typography color="error" variant="h4">
                Delete
              </Typography>
            </Button>
            <Button
              data-testid="view-pref-card-button"
              onClick={() => handleCardClick(item.id)}
              endIcon={<ReadMore />}
            >
              <Typography variant="h4">View</Typography>
            </Button>
            <Button
              data-testid="clone-pref-card-button"
              onClick={() => handleCloneClick(item.id)}
              endIcon={<ContentCopy />}
            >
              <Typography variant="h4">Clone</Typography>
            </Button>
          </div>
        ),
      })) as TableRowProps[],
    [rawPreferenceCards, handleCardClick, handleCloneClick]
  )

  const isLoadingSearch = isLoadingPrefCards && searchName.length > 0

  const handleUnassignClick = useCallback(
    (surgeryId: string, preferenceCardId: string) => {
      setIsAlertOpen({
        isOpen: true,
        surgeryId,
        id: preferenceCardId,
        type: 'unassign',
      })
    },
    []
  )

  const handleOpenAssignModal = useCallback((surgery: SurgeryData) => {
    setSelectedSurgery(surgery)
    setIsAssignModalOpen(true)
  }, [])

  const renderProcedureActions = useCallback(
    (item: SurgeryData) => (
      <div className="table-actions">
        {item.preferenceCardId ? (
          <>
            <Button
              data-testid="delete-pref-card-button"
              onClick={() =>
                handleUnassignClick(item.surgeryId, item.preferenceCardId)
              }
              endIcon={<Delete color="error" />}
            >
              <Typography color="error" variant="h4">
                Unassign
              </Typography>
            </Button>
            <Button
              data-testid="view-pref-card-button"
              onClick={() => handleCardClick(item.preferenceCardId)}
              endIcon={<ReadMore />}
            >
              <Typography variant="h4">View</Typography>
            </Button>
          </>
        ) : (
          <>
            <Button
              data-testid="assign-pref-card-button"
              onClick={() => handleOpenAssignModal(item)}
              endIcon={<AssignmentInd />}
            >
              <Typography variant="h4">Assign</Typography>
            </Button>
          </>
        )}
      </div>
    ),
    [handleUnassignClick, handleCardClick, handleOpenAssignModal]
  )

  const proceduresData = useMemo(
    () =>
      procedures?.surgeries.map((item) => ({
        id: item.surgeryId,
        date: dayjs(item?.procedures[0]?.dateTime).format('MMM DD YYYY'),
        surgeon: `${item?.surgeonLastName}, ${item?.surgeonFirstName}`,
        procedure: item.procedures[0]?.description || '',
        preferenceCard: item.preferenceCardId ? (
          <Typography variant="body1" color="primary">
            {item.preferenceCardName || item.procedures[0]?.description}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No card assigned
          </Typography>
        ),
        actions: renderProcedureActions(item),
      })) as TableRowProps[],
    [procedures?.surgeries, renderProcedureActions]
  )

  // Add the fetch products function
  const fetchProductsByIds = async (ids: number[]) => {
    const queryString = `productsIds=${ids.map(encodeURIComponent).join(',')}`
    const url = `operations/assets/find-by-products-Ids?${queryString}`
    return await createBetterIdRequest(url, {}, 'GET')
  }

  // Handlers
  const handleDelete = async () => {
    try {
      await deletePrefCard({ id: isAlertOpen.id as string })
      queryClient.invalidateQueries('preferenceCards')
      queryClient.invalidateQueries('surgeries')
      setIsAlertOpen({ isOpen: false, type: 'delete' })
      enqueueSnackbar('Preference card has been deleted.', {
        variant: 'success',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleUnassign = async () => {
    try {
      await unAssignPrefCard({ surgeryId: isAlertOpen.surgeryId as string })
      queryClient.invalidateQueries('surgeries')
      setIsAlertOpen({ isOpen: false, type: 'unassign' })
      enqueueSnackbar('Preference card has been unassigned.', {
        variant: 'success',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
    } catch (error) {
      enqueueSnackbar('Failed to unassign preference card.', {
        variant: 'error',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
      console.error(error)
    }
  }

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setAfterDate(startDate)
    setBeforeDate(endDate)
  }

  const handleCreatePrefCard = (
    hasCredentials: boolean,
    surgeonId?: string,
    procedureType?: string
  ) => {
    navigate(
      `/preference-cards/new`,
      hasCredentials ? { state: { surgeonId, procedureType } } : {}
    )
  }

  const handleAlertAction = () => {
    if (isAlertOpen.type === 'delete') {
      handleDelete()
    } else {
      handleUnassign()
    }
  }

  const handleCloseAssignModal = () => {
    queryClient.invalidateQueries('surgeries')
    setSelectedSurgery(null)
    setIsAssignModalOpen(false)
  }

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    if (activeTab === 0) {
      setProcedurePage(newPage)
    } else {
      setPrefCardPage(newPage)
    }
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    if (activeTab === 0) {
      setProcedureRowsPerPage(newRowsPerPage)
      setProcedurePage(0)
      // Clear selections when changing rows per page
      setSelectedProcedureRows([])
      setSelectedProcedureData([])
    } else {
      setPrefCardRowsPerPage(newRowsPerPage)
      setPrefCardPage(0)
      // Clear selections when changing rows per page
      setSelectedPrefCardRows([])
      setSelectedPrefCardData([])
    }
  }

  // Maintain selections across pagination by combining current page selections with previous selections
  const handleRowSelect = (selectedIds: string[]) => {
    if (activeTab === 0) {
      setSelectedProcedureRows(selectedIds)
      setSelectedProcedureData((prev) => {
        // Get current page selections
        const currentPageSelections =
          procedures?.surgeries.filter((procedure) =>
            selectedIds.includes(procedure.surgeryId)
          ) || []
        // Keep selections from other pages
        const otherPagesSelections = prev.filter(
          (procedure) =>
            !procedures?.surgeries.some(
              (current) => current.surgeryId === procedure.surgeryId
            )
        )
        // Combine selections
        return [...otherPagesSelections, ...currentPageSelections]
      })
    } else {
      // Similar logic for preference cards
      setSelectedPrefCardRows(selectedIds)
      setSelectedPrefCardData((prev) => {
        const currentPageSelections = rawPreferenceCards.filter((card) =>
          selectedIds.includes(card.id)
        )
        const otherPagesSelections = prev.filter(
          (card) =>
            !rawPreferenceCards.some((current) => current.id === card.id)
        )
        return [...otherPagesSelections, ...currentPageSelections]
      })
    }
  }

  // Main export handler that routes to the appropriate export function based on active tab
  const handleExportSelected = async () => {
    try {
      setIsGeneratingPDF(true)
      if (activeTab === 0) {
        // Handle selected procedures
        if (selectedProcedureData.length === 0) {
          enqueueSnackbar('Please select at least one procedure to export', {
            variant: 'warning',
            autoHideDuration: 3000,
            preventDuplicate: true,
          })
          return
        }

        // Fetch all preference cards
        const { data: prefCards } = await refetchAllPrefCards()
        if (!prefCards?.preference_cards) {
          enqueueSnackbar('Failed to fetch preference cards', {
            variant: 'error',
            autoHideDuration: 3000,
            preventDuplicate: true,
          })
          return
        }

        // Create a map of preference cards
        const prefCardsMap = prefCards.preference_cards.reduce(
          (acc: any, card: any) => {
            acc[card.id] = card
            return acc
          },
          {}
        )

        // Extract all product IDs
        const allProductIds = new Set<number>()
        prefCards.preference_cards.forEach((card: any) => {
          card.card_details?.consumables?.items?.forEach((item: any) => {
            if (item.bidProductId) {
              allProductIds.add(item.bidProductId)
            }
          })
        })

        // Fetch products
        const productsData = await fetchProductsByIds(Array.from(allProductIds))
        if (!productsData?.data?.products) {
          enqueueSnackbar('Failed to fetch products', {
            variant: 'error',
            autoHideDuration: 3000,
            preventDuplicate: true,
          })
          return
        }

        // Format cards with surgery info - only include procedures that have preference cards
        const formattedCards = selectedProcedureData
          .map((procedure) => {
            const prefCard = prefCardsMap[procedure.preferenceCardId]
            if (!prefCard) {
              console.warn(
                `No preference card found for procedure ${procedure.surgeryId}`
              )
              return null
            }
            return {
              ...prefCard,
              card_details: {
                ...prefCard.card_details,
                consumables: {
                  ...prefCard.card_details?.consumables,
                  items:
                    prefCard.card_details?.consumables?.items?.map(
                      (item: any) => ({
                        ...item,
                        bidProductId: item.bidProductId,
                        qty:
                          item.qty && typeof item.qty === 'object'
                            ? item.qty.value || item.qty
                            : item.qty,
                        measurement:
                          item.measurement &&
                          typeof item.measurement === 'object'
                            ? item.measurement.value || item.measurement
                            : item.measurement,
                      })
                    ) || [],
                },
                companies: {
                  biologic: prefCard.card_details?.companies?.biologic || [],
                  implants: prefCard.card_details?.companies?.implants || [],
                },
              },
              surgeryInfo: {
                dateTime:
                  procedure.visitDate ||
                  procedure.procedures[0]?.dateTime ||
                  '',
                surgeonLastName: procedure.surgeonLastName || '',
                surgeonFirstName: procedure.surgeonFirstName || '',
                description: procedure.procedures[0]?.description || '',
                patientMRN: procedure.patientMRN || '',
                patientLastName: procedure.patientLastName || '',
                patientFirstName: procedure.patientFirstName || '',
              },
            }
          })
          .filter(Boolean) // Remove any null entries

        if (formattedCards.length === 0) {
          enqueueSnackbar('No valid preference cards found to export', {
            variant: 'warning',
            autoHideDuration: 3000,
            preventDuplicate: true,
          })
          return
        }

        // Generate single PDF
        const pdfDoc = pdf(
          <PreferenceCardsPDF
            preferenceCards={formattedCards}
            products={productsData.data.products}
            showSurgeryInfo={true}
            showPascPreferenceCardFeatures={showPascPreferenceCardFeatures}
          />
        )

        // Convert to blob
        const blob = await pdfDoc.toBlob()

        // Create URL for the blob
        const url = window.URL.createObjectURL(blob)

        // Open PDF in new window
        const newWindow = window.open(url, '_blank')

        // Clean up URL after window is closed
        if (newWindow) {
          newWindow.onbeforeunload = () => {
            window.URL.revokeObjectURL(url)
          }
        }

        enqueueSnackbar(
          `Successfully generated PDF with ${selectedProcedureData.length} procedures`,
          {
            variant: 'success',
            autoHideDuration: 3000,
            preventDuplicate: true,
          }
        )
      } else {
        // Handle selected preference cards
        if (selectedPrefCardData.length === 0) {
          enqueueSnackbar(
            'Please select at least one preference card to export',
            {
              variant: 'warning',
              autoHideDuration: 3000,
              preventDuplicate: true,
            }
          )
          return
        }

        // Extract all product IDs
        const allProductIds = new Set<number>()
        selectedPrefCardData.forEach((card) => {
          card.card_details?.consumables?.items?.forEach((item: any) => {
            if (item.bidProductId) {
              allProductIds.add(item.bidProductId)
            }
          })
        })

        // Fetch products
        const productsData = await fetchProductsByIds(Array.from(allProductIds))
        if (!productsData?.data?.products) {
          enqueueSnackbar('Failed to fetch products', {
            variant: 'error',
            autoHideDuration: 3000,
            preventDuplicate: true,
          })
          return
        }

        // Format cards
        const formattedCards = selectedPrefCardData
          .map((card) => ({
            ...card,
            card_details: {
              ...card.card_details,
              consumables: {
                ...card.card_details?.consumables,
                items:
                  card.card_details?.consumables?.items?.map((item: any) => ({
                    ...item,
                    bidProductId: item.bidProductId,
                    qty:
                      item.qty && typeof item.qty === 'object'
                        ? item.qty.value || item.qty
                        : item.qty,
                    measurement:
                      item.measurement && typeof item.measurement === 'object'
                        ? item.measurement.value || item.measurement
                        : item.measurement,
                  })) || [],
              },
              companies: {
                biologic: card.card_details?.companies?.biologic || [],
                implants: card.card_details?.companies?.implants || [],
              },
              surgeonInformation: card.card_details?.surgeonInformation || {},
              equipment: card.card_details?.equipment || {},
              instrumentTrays: card.card_details?.instrumentTrays || {},
              linkedSalesReps: card.card_details?.linkedSalesReps || [],
            },
          }))
          .filter((card) => card && card.card_details) // Filter out any invalid cards

        // Generate single PDF
        const pdfDoc = pdf(
          <PreferenceCardsPDF
            preferenceCards={formattedCards}
            products={productsData.data.products}
            showPascPreferenceCardFeatures={showPascPreferenceCardFeatures}
          />
        )

        // Convert to blob
        const blob = await pdfDoc.toBlob()

        // Create URL for the blob
        const url = window.URL.createObjectURL(blob)

        // Open PDF in new window
        const newWindow = window.open(url, '_blank')

        // Clean up URL after window is closed
        if (newWindow) {
          newWindow.onbeforeunload = () => {
            window.URL.revokeObjectURL(url)
          }
        }

        enqueueSnackbar(
          `Successfully generated PDF with ${selectedPrefCardData.length} preference cards`,
          {
            variant: 'success',
            autoHideDuration: 3000,
            preventDuplicate: true,
          }
        )
      }
    } catch (error) {
      enqueueSnackbar('Error generating PDF: ' + (error as Error).message, {
        variant: 'error',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  // Get pagination data based on active tab
  const paginationData = useMemo(() => {
    if (activeTab === 0) {
      return {
        page: procedurePage,
        rowsPerPage: procedureRowsPerPage,
        totalCount: procedures?.totalCount || 0,
      }
    }
    return {
      page: prefCardPage,
      rowsPerPage: prefCardRowsPerPage,
      totalCount: prefCards?.totalCount || 0,
    }
  }, [
    activeTab,
    procedurePage,
    prefCardPage,
    procedureRowsPerPage,
    prefCardRowsPerPage,
    procedures?.totalCount,
    prefCards?.totalCount,
  ])

  // Handle tab change
  const handleTabChange = (newTab: number) => {
    setActiveTab(newTab)
    localStorage.setItem('preferenceCardsActiveTab', newTab.toString())
    // Don't clear selections when switching tabs
  }

  // Update localStorage when tab changes
  useEffect(() => {
    localStorage.setItem('preferenceCardsActiveTab', activeTab.toString())
  }, [activeTab])
  useEffect(() => {
    const debounced = debounce(() => {
      setSearchParams({
        surgeonName: searchSurgeon,
        procedureType: searchProcedure,
        name: searchName,
      })
    }, 500)
    debounced()
  }, [searchSurgeon, searchProcedure, searchName])

  const handleExportAll = async () => {
    try {
      setExportAllPrefCardsState((prev) => ({
        ...prev,
        all: true,
      }))
      setIsGeneratingPDF(true)

      // Fetch all preference cards
      const { data: prefCards } = await refetchAllPrefCards()
      if (!prefCards?.preference_cards) {
        enqueueSnackbar('No preference cards to export', {
          variant: 'warning',
          autoHideDuration: 3000,
          preventDuplicate: true,
        })
        return
      }

      // Extract all product IDs
      const allProductIds = new Set<number>()
      prefCards.preference_cards.forEach((card: any) => {
        card.card_details?.consumables?.items?.forEach((item: any) => {
          if (item.bidProductId) {
            allProductIds.add(item.bidProductId)
          }
        })
      })

      // Fetch products
      const productsData = await fetchProductsByIds(Array.from(allProductIds))
      if (!productsData?.data?.products) {
        enqueueSnackbar('Failed to fetch products', {
          variant: 'error',
          autoHideDuration: 3000,
          preventDuplicate: true,
        })
        return
      }

      // Format all cards
      const formattedCards = prefCards.preference_cards
        .map((card) => ({
          ...card,
          card_details: {
            ...card.card_details,
            consumables: {
              ...card.card_details?.consumables,
              items:
                card.card_details?.consumables?.items?.map((item: any) => ({
                  ...item,
                  bidProductId: item.bidProductId,
                  qty:
                    item.qty && typeof item.qty === 'object'
                      ? item.qty.value || item.qty
                      : item.qty,
                  measurement:
                    item.measurement && typeof item.measurement === 'object'
                      ? item.measurement.value || item.measurement
                      : item.measurement,
                })) || [],
            },
            companies: {
              biologic: card.card_details?.companies?.biologic || [],
              implants: card.card_details?.companies?.implants || [],
            },
            surgeonInformation: card.card_details?.surgeonInformation || {},
            equipment: card.card_details?.equipment || {},
            instrumentTrays: card.card_details?.instrumentTrays || {},
            linkedSalesReps: card.card_details?.linkedSalesReps || [],
          },
        }))
        .filter((card) => card && card.card_details) // Filter out any invalid cards

      // Generate single PDF with all cards
      const pdfDoc = pdf(
        <PreferenceCardsPDF
          preferenceCards={formattedCards}
          products={productsData.data.products}
          showPascPreferenceCardFeatures={showPascPreferenceCardFeatures}
        />
      )

      // Convert to blob
      const blob = await pdfDoc.toBlob()

      // Create URL for the blob
      const url = window.URL.createObjectURL(blob)

      // Open PDF in new window
      const newWindow = window.open(url, '_blank')

      // Clean up URL after window is closed
      if (newWindow) {
        newWindow.onbeforeunload = () => {
          window.URL.revokeObjectURL(url)
        }
      }

      enqueueSnackbar(
        `Successfully generated PDF with ${formattedCards.length} preference cards`,
        {
          variant: 'success',
          autoHideDuration: 3000,
          preventDuplicate: true,
        }
      )
    } catch (error) {
      enqueueSnackbar('Error generating PDF: ' + (error as Error).message, {
        variant: 'error',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
    } finally {
      setExportAllPrefCardsState((prev) => ({
        ...prev,
        all: false,
      }))
      setIsGeneratingPDF(false)
    }
  }

  const handleExportProcedures = async (dateRange: DateRangeType) => {
    try {
      // Set the specific loading state based on dateRange
      setExportState((prev) => ({
        ...prev,
        [dateRange === 'custom' ? 'dateRange' : dateRange]: true,
      }))

      let startDate: dayjs.Dayjs
      let endDate: dayjs.Dayjs
      let refetchFunction: typeof refetchTodayProcedures
      let dateRangeMessage: string

      switch (dateRange) {
        case 'today':
          startDate = dayjs().startOf('day')
          endDate = dayjs().endOf('day')
          refetchFunction = refetchTodayProcedures
          dateRangeMessage = 'today'
          break
        case 'tomorrow':
          startDate = dayjs().add(1, 'day').startOf('day')
          endDate = dayjs().add(1, 'day').endOf('day')
          refetchFunction = refetchTomorrowProcedures
          dateRangeMessage = 'tomorrow'
          break
        case 'custom':
          startDate = dayjs(afterDate).startOf('day')
          endDate = dayjs(beforeDate).endOf('day')
          refetchFunction = refetchDateRangeProcedures
          dateRangeMessage = 'date range'
          break
        default:
          startDate = dayjs().startOf('day')
          endDate = dayjs().endOf('day')
          refetchFunction = refetchTodayProcedures
          dateRangeMessage = 'today'
      }

      const { data: procedures } = await refetchFunction()

      // Check if there are any procedures
      if (!procedures?.surgeries || procedures.surgeries.length === 0) {
        enqueueSnackbar(`No procedures found for ${dateRangeMessage}`, {
          variant: 'info',
          autoHideDuration: 3000,
          preventDuplicate: true,
        })
        return
      }

      // Check if any procedures have preference cards
      const proceduresWithPrefCards = procedures.surgeries.filter(
        (procedure) => procedure.preferenceCardId
      )

      if (proceduresWithPrefCards.length === 0) {
        enqueueSnackbar(
          `No procedures with preference cards found for ${dateRangeMessage}`,
          {
            variant: 'info',
            autoHideDuration: 3000,
            preventDuplicate: true,
          }
        )
        return
      }

      // Fetch all preference cards
      const { data: prefCards } = await refetchAllPrefCards()
      if (!prefCards?.preference_cards) {
        enqueueSnackbar('Failed to fetch preference cards', {
          variant: 'error',
          autoHideDuration: 3000,
          preventDuplicate: true,
        })
        return
      }

      // Create a map of preference cards
      const prefCardsMap = prefCards.preference_cards.reduce(
        (acc: any, card: any) => {
          acc[card.id] = card
          return acc
        },
        {}
      )

      // Extract all product IDs
      const allProductIds = new Set<number>()
      prefCards.preference_cards.forEach((card: any) => {
        card.card_details?.consumables?.items?.forEach((item: any) => {
          if (item.bidProductId) {
            allProductIds.add(item.bidProductId)
          }
        })
      })

      // Fetch products
      const productsData = await fetchProductsByIds(Array.from(allProductIds))
      if (!productsData?.data?.products) {
        enqueueSnackbar('Failed to fetch products', {
          variant: 'error',
          autoHideDuration: 3000,
          preventDuplicate: true,
        })
        return
      }

      // Format cards with surgery info - only include procedures that have preference cards
      const formattedCards = proceduresWithPrefCards
        .map((procedure) => {
          const prefCard = prefCardsMap[procedure.preferenceCardId]
          if (!prefCard) {
            console.warn(
              `No preference card found for procedure ${procedure.surgeryId}`
            )
            return null
          }
          return {
            ...prefCard,
            card_details: {
              ...prefCard.card_details,
              consumables: {
                ...prefCard.card_details?.consumables,
                items:
                  prefCard.card_details?.consumables?.items?.map(
                    (item: any) => ({
                      ...item,
                      bidProductId: item.bidProductId,
                      qty:
                        item.qty && typeof item.qty === 'object'
                          ? item.qty.value || item.qty
                          : item.qty,
                      measurement:
                        item.measurement && typeof item.measurement === 'object'
                          ? item.measurement.value || item.measurement
                          : item.measurement,
                    })
                  ) || [],
              },
              companies: {
                biologic: prefCard.card_details?.companies?.biologic || [],
                implants: prefCard.card_details?.companies?.implants || [],
              },
            },
            surgeryInfo: {
              dateTime:
                procedure.visitDate || procedure.procedures[0]?.dateTime || '',
              surgeonLastName: procedure.surgeonLastName || '',
              surgeonFirstName: procedure.surgeonFirstName || '',
              description: procedure.procedures[0]?.description || '',
              patientMRN: procedure.patientMRN || '',
              patientLastName: procedure.patientLastName || '',
              patientFirstName: procedure.patientFirstName || '',
            },
          }
        })
        .filter(Boolean) // Remove any null entries

      if (formattedCards.length === 0) {
        enqueueSnackbar('No valid preference cards found to export', {
          variant: 'warning',
          autoHideDuration: 3000,
          preventDuplicate: true,
        })
        return
      }

      // Generate single PDF
      const pdfDoc = pdf(
        <PreferenceCardsPDF
          preferenceCards={formattedCards}
          products={productsData.data.products}
          showSurgeryInfo={true}
          showPascPreferenceCardFeatures={showPascPreferenceCardFeatures}
        />
      )

      // Convert to blob
      const blob = await pdfDoc.toBlob()

      // Create URL for the blob
      const url = window.URL.createObjectURL(blob)

      // Open PDF in new window
      const newWindow = window.open(url, '_blank')

      // Clean up URL after window is closed
      if (newWindow) {
        newWindow.onbeforeunload = () => {
          window.URL.revokeObjectURL(url)
        }
      }

      enqueueSnackbar(
        `Successfully generated PDF with ${formattedCards.length} procedures`,
        {
          variant: 'success',
          autoHideDuration: 3000,
          preventDuplicate: true,
        }
      )
    } catch (error) {
      enqueueSnackbar('Error generating PDF: ' + (error as Error).message, {
        variant: 'error',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
    } finally {
      // Reset all loading states
      setExportState({
        today: false,
        tomorrow: false,
        dateRange: false,
      })
    }
  }

  const handleExportTodaySurgeries = () => handleExportProcedures('today')
  const handleExportTomorrowSurgeries = () => handleExportProcedures('tomorrow')
  const handleExportDateRange = () => handleExportProcedures('custom')

  return {
    ...paginationData,
    isError,
    afterDate,
    beforeDate,
    setBeforeDate,
    setAfterDate,
    activeTab,
    searchName,
    isAlertOpen,
    searchSurgeon,
    proceduresData,
    procedures,
    isLoadingAction: isUnAssigning || isDeleting,
    isLoadingSearch,
    isErrorPrefCard,
    surgeonsOptions,
    searchProcedure,
    preferenceCards,
    selectedSurgery,
    prefCardHeadCells,
    isAssignModalOpen,
    isLoadingSurgeons,
    isLoadingPrefCards,
    isLoadingProcedures,
    proceduresHeadCells,
    procedureTypesOptions,
    isLoadingProcedureTypes,
    selectedProcedureData,
    selectedProcedureRows,
    selectedPrefCardRows,
    selectedPrefCardData,
    isExportingToday: exportState.today,
    isExportingTomorrow: exportState.tomorrow,
    isExportingDateRange: exportState.dateRange,
    exportAllPrefCardsState,
    showPascPreferenceCardFeatures,
    setSearchName,
    setActiveTab: handleTabChange,
    setIsAlertOpen,
    setSearchSurgeon,
    handlePageChange,
    handleAlertAction,
    setSearchProcedure,
    handleCreatePrefCard,
    handleCloseAssignModal,
    handleRowsPerPageChange,
    handleRowSelect,
    setSelectedProcedureRows,
    setSelectedProcedureData,
    handleExportAll,
    handleExportSelected,
    handleDateRangeChange,
    handleExportTodaySurgeries,
    handleExportTomorrowSurgeries,
    handleExportDateRange,
    setSelectedPrefCardRows,
    setSelectedPrefCardData,
    isGeneratingPDF,
  }
}

export default useLogic
